import { __decorate, __metadata } from "tslib";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { setInteractionMode } from "vee-validate";
import Simplebar from "simplebar-vue";
import Multiselect from "vue-multiselect";
import { Guid } from "guid-typescript";
import ClientDetailsMixin from "../ClientDetailsMixin";
import StrictDateMixin from "@/router/views/StrictDateMixin";
import { toaster } from "@/utilities/toaster";
import ClientModule from "@/store/modules/client.module";
import { PMOTeamModel } from "@/store/models/client/pmoTeam";
import { PMOEditTeamModel } from "@/store/models/client/pmoEditTeam";
import { PMOTeamMemberModel } from "@/store/models/client/pmoTeamMember";
import { PMOTeamLeadModel } from "@/store/models/client/pmoTeamLead";
import { PMOProjectModel } from "@/store/models/project/PMOProject";
import { ClientTeamDeactivationReasonModel } from "@/store/models/client/clientTeamDeactivationReason";
import DeleteComponent from "@/components/deleteComponent.vue";
import ECAlert from "@/components/ECAlert.vue";
import Loader from "@/components/widgets/loader.vue";
import SectionLoader from "@/components/widgets/SectionLoader.vue";
import { parseStrictDate } from "@/utilities/strictDateFormatter";
import { RECHECK_MODE } from "@/types/validators";
import TeamOperationsComponent from "./teamOperations/TeamOperationsComponent.vue";
import TeamCorrespondenceComponent from "./teamCorrespondence/TeamCorrespondenceComponent.vue";
import { askToDiscardChanges, discardUnsavedChanges, hasUnsavedChanges } from "@/utilities/unsavedChanges";
setInteractionMode("passive");
const MEMBER_START_BEFORE_CLIENT_START = "Client Hire Date should be the same as or later than the client's Start Date.";
const MEMBER_START_AFTER_END = "Client Hire Date should be lesser than End Date.";
/**
 * Client Details — Teams tab. Team cards, team detail (Members / Operations / Correspondences / Projects) and
 * every team, member and Apex project modal.
 */
let ClientTeamsTabComponent = /** @class */ (() => {
    var _a;
    let ClientTeamsTabComponent = class ClientTeamsTabComponent extends Mixins(ClientDetailsMixin, StrictDateMixin) {
        constructor() {
            super(...arguments);
            this.recheckMode = RECHECK_MODE;
            this.team = new PMOTeamModel();
            this.editTeam = new PMOEditTeamModel();
            this.teamMember = new PMOTeamMemberModel();
            this.selectedEligibleMember = null;
            // Team detail tab: Members, Operations, Correspondence, Project
            this.teamTabIndex = 0;
            // The card whose details are being fetched, so its loader stays on that card.
            this.viewingTeamId = "";
            this.memberNotesToView = "";
            this.memberNotesEmployeeName = "";
            this.memberNotesPopoverVisible = false;
            this.memberNotesPopoverTarget = "";
            this.teamLead = new PMOTeamLeadModel();
            this.editApexProject = new PMOProjectModel();
            this.newProject = new PMOProjectModel();
            this.showDeleteMemberModal = false;
            this.showDeleteTeamModal = false;
            this.deactivatedReason = "";
            this.deactivationDate = null;
            this.memberAvatarLimit = 5;
        }
        // Each modal is open only while the machine is in its state.
        onStateChange(state) {
            const region = "loadedClientContactsInfo.contactAndTeamInfo.";
            this.toggleModal("add-team", state.matches(region + "addTeamInfo"));
            this.toggleModal("edit-team", state.matches(region + "updateTeamInfo"));
            // Add Member stays open (with its own loader) while the save is in flight; it closes only on success.
            this.toggleModal("add-member", state.matches(region + "addTeamMember") || state.matches(region + "addingTeamMember"));
            this.toggleModal("edit-member", state.matches(region + "updateTeamMember"));
            this.toggleModal("add-project", state.matches(region + "addApexProject"));
            this.toggleModal("edit-project", state.matches(region + "updateApexProject"));
            this.toggleModal("reason-for-deactivation-team", state.matches(region + "deactivateTeamInfo"));
        }
        toggleModal(id, visible) {
            if (visible) {
                this.$bvModal.show(id);
            }
            else {
                this.$bvModal.hide(id);
            }
        }
        // Teams
        viewTeam(teamId) {
            this.teamTabIndex = 0;
            this.viewingTeamId = teamId;
            this.machineService.send({ type: "VIEW_TEAM", teamId: teamId });
        }
        // Leaving the team view throws away unsaved Correspondence edits, so ask first
        backtoTeam() {
            if (hasUnsavedChanges()) {
                askToDiscardChanges().then((discard) => {
                    if (discard) {
                        discardUnsavedChanges();
                        this.machineService.send({ type: "BACK_TO_TEAMS" });
                    }
                });
                return;
            }
            this.machineService.send({ type: "BACK_TO_TEAMS" });
        }
        // Team detail tabs are lazy, so switching away from Correspondence destroys its unsaved edits - ask first.
        // The tab's @click has already moved the overview machine; Keep editing moves it back.
        onTeamTabChange(newTabIndex, prevTabIndex, bvEvent) {
            if (!hasUnsavedChanges()) {
                return;
            }
            bvEvent.preventDefault();
            askToDiscardChanges().then((discard) => {
                if (discard) {
                    discardUnsavedChanges();
                    this.teamTabIndex = newTabIndex;
                }
                else {
                    this.machineService.send({ type: "VIEW_TEAM_CORRESPONDENCE" });
                }
            });
        }
        addTeam() {
            this.team = new PMOTeamModel();
            this.machineService.send({ type: "ADD_TEAM" });
        }
        cancelTeam() {
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        saveTeam() {
            this.validate("newTeamObserver").then((res) => {
                if (res) {
                    this.team.clientId = this.clientId.toString();
                    this.team.name = (this.team.name || "").trim();
                    this.machineService.send({ type: "ADDING_TEAM", payLoad: this.team });
                    this.team = new PMOTeamModel();
                }
            });
        }
        showEditTeamModal(team) {
            this.team.id = Guid.parse(team.teamId);
            this.team.clientId = this.clientId.toString();
            this.team.name = team.teamName;
            this.machineService.send({ type: "UPDATE_TEAM" });
        }
        cancelEditTeam() {
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        updateTeam(team) {
            this.validate("editTeamObserver").then((res) => {
                if (res) {
                    this.editTeam.id = team.id.value;
                    this.editTeam.name = (team.name || "").trim();
                    this.editTeam.clientId = this.clientId.toString();
                    this.machineService.send({
                        type: "UPDATING_TEAM",
                        payLoad: this.editTeam,
                    });
                    this.editTeam = new PMOEditTeamModel();
                }
            });
        }
        showDeactivate(teamId) {
            this.delTeamValueId = teamId;
            this.deactivatedReason = "";
            // Deactivation Date defaults to today (left empty if the client only starts later)
            const today = this.toCalendarDay(new Date());
            this.deactivationDate = this.isBeforeClientStartDate(today, this.client ? this.client.startDate : null) ? null : today;
            this.clearDateErrors();
            this.machineService.send({ type: "DEACTIVATE_TEAM" });
        }
        // Deactivation Date picker: no future days, nothing before the client's Start Date
        disableDeactivationDate(date) {
            return date > this.toCalendarDay(new Date()) || this.isBeforeClientStartDate(date, this.client ? this.client.startDate : null);
        }
        deActivateTeam() {
            this.validate("teamDeactivationObserver").then((res) => {
                if (res && !this.hasDateErrors) {
                    const reason = new ClientTeamDeactivationReasonModel();
                    reason.teamId = this.delTeamValueId;
                    reason.deactivatedReason = this.deactivatedReason;
                    reason.deactivationDateAsString = this.formatClientDate(this.deactivationDate);
                    this.machineService.send({
                        type: "DEACTIVATING_TEAM",
                        deactivationReason: reason,
                    });
                }
            });
        }
        cancelDeactivation() {
            this.delTeamValueId = "";
            this.deactivatedReason = "";
            this.deactivationDate = null;
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        showActivate(teamId) {
            this.delTeamValueId = teamId;
            this.machineService.send({ type: "ACTIVATE_TEAM" });
        }
        activatingTeam() {
            this.machineService.send({ type: "ACTIVATING_TEAM", teamId: this.delTeamValueId });
        }
        cancelActivate() {
            this.delTeamValueId = "";
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        openDeleteTeamModal(valueId) {
            this.delTeamValueId = valueId;
            this.showDeleteTeamModal = true;
            this.machineService.send({ type: "DELETE_TEAM" });
        }
        deleteTeam() {
            this.machineService.send({
                type: "DELETING_TEAM",
                payLoad: this.delTeamValueId,
            });
            this.showDeleteTeamModal = false;
        }
        // Members
        showMemberList() {
            this.machineService.send({ type: "VIEW_MEMBER" });
        }
        addMember(teamId) {
            this.teamMember = new PMOTeamMemberModel();
            this.selectedEligibleMember = null;
            this.clearDateErrors();
            this.machineService.send({ type: "ADD_MEMBER", teamId: teamId });
        }
        onEligibleMemberSelected(member) {
            this.selectedEligibleMember = member;
            this.teamMember.employeeId = member && member.id ? member.id.toString() : "";
            // EC Hire Date follows the picked member (read-only)
            this.teamMember.dateOfJoinAsString = member && member.dateOfJoinAsString ? member.dateOfJoinAsString : "";
        }
        cancelMember() {
            this.selectedEligibleMember = null;
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        // Member Start Date: no day before the client's Start Date (same day allowed; no Start Date = no limit)
        disableBeforeClientStart(date) {
            return this.isBeforeClientStartDate(date, this.client ? this.client.startDate : null);
        }
        saveMember() {
            this.validate("newTeamMemberObserver").then((res) => {
                if (res && !this.hasDateErrors) {
                    if (this.disableBeforeClientStart(this.teamMember.startDate)) {
                        toaster.ShowError(MEMBER_START_BEFORE_CLIENT_START);
                    }
                    else if (this.teamMember.endDate != null && this.teamMember.startDate > this.teamMember.endDate) {
                        toaster.ShowError(MEMBER_START_AFTER_END);
                    }
                    else {
                        var member = this.selectedEligibleMember || this.eligableTeamMember.find((x) => x.id == Guid.parse(this.teamMember.employeeId));
                        this.teamMember.teamId = this.teamDetailView.id.toString();
                        this.teamMember.employeeId = member.id.toString();
                        this.teamMember.employeeName = member.value;
                        this.teamMember.startDateAsString = this.dateFormattedString(this.teamMember.startDate);
                        if (this.teamMember.endDate) {
                            this.teamMember.endDateAsString = this.dateFormattedString(this.teamMember.endDate);
                        }
                        this.machineService.send({ type: "ADDING_MEMBER", payLoad: this.teamMember });
                    }
                }
            });
        }
        showEditMember(teamMember) {
            this.teamMember.id = teamMember.id;
            this.teamMember.employeeId = teamMember.employeeId;
            this.teamMember.employeeName = teamMember.employeeName;
            this.teamMember.startDateAsString = teamMember.startDateAsString;
            this.teamMember.endDateAsString = teamMember.endDateAsString;
            this.teamMember.dateOfJoinAsString = teamMember.dateOfJoinAsString;
            this.teamMember.clientProvidedEmail = teamMember.clientProvidedEmail;
            this.teamMember.engagementComments = teamMember.engagementComments;
            this.teamMember.isTeamLead = teamMember.isTeamLead;
            this.clearDateErrors();
            this.machineService.send({ type: "UPDATE_MEMBER" });
        }
        cancelEditMember() {
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        updateMember() {
            this.validate("editTeamMemberObserver").then((res) => {
                if (res && !this.hasDateErrors) {
                    if (this.disableBeforeClientStart(parseStrictDate(this.teamMember.startDateAsString, this.dateFormat))) {
                        toaster.ShowError(MEMBER_START_BEFORE_CLIENT_START);
                    }
                    else if (new Date(Date.parse(this.teamMember.startDateAsString)) > new Date(Date.parse(this.teamMember.endDateAsString))) {
                        toaster.ShowError(MEMBER_START_AFTER_END);
                    }
                    else {
                        this.teamMember.teamId = this.teamDetailView.id.toString();
                        this.machineService.send({ type: "UPDATING_MEMBER", payLoad: this.teamMember });
                    }
                }
            });
        }
        openDeleteMemberModal(valueId) {
            this.delValueId = valueId;
            this.showDeleteMemberModal = true;
            this.machineService.send({ type: "DELETE_MEMBER" });
        }
        deleteTeamMember() {
            this.machineService.send({
                type: "DELETING_MEMBER",
                teamMemberId: this.delValueId,
            });
            this.showDeleteMemberModal = false;
        }
        setAsTeamLead(team) {
            this.teamLead.teamId = this.teamDetailView.id.toString();
            this.teamLead.employeeId = team.employeeId;
            ClientModule.AssignTeamLead(this.teamLead);
        }
        removeTeamLead(team) {
            this.teamLead.teamId = this.teamDetailView.id.toString();
            this.teamLead.employeeId = team.employeeId;
            ClientModule.RemoveTeamLead(this.teamLead);
        }
        viewMemberNotes(teamMember) {
            this.memberNotesToView = teamMember.engagementComments;
            this.memberNotesEmployeeName = teamMember.employeeName;
            this.memberNotesPopoverTarget = `member-notes-${teamMember.id}`;
            this.memberNotesPopoverVisible = true;
        }
        closeMemberNotes() {
            this.memberNotesPopoverVisible = false;
        }
        // Apex projects
        showApexProjects() {
            this.machineService.send({ type: "VIEW_APEX_PROJECTS" });
        }
        showTeamOperations() {
            this.machineService.send({ type: "VIEW_TEAM_OPERATIONS" });
        }
        showTeamCorrespondence() {
            this.machineService.send({ type: "VIEW_TEAM_CORRESPONDENCE" });
        }
        addProject() {
            this.newProject = new PMOProjectModel();
            this.machineService.send({ type: "ADD_APEX_PROJECT" });
        }
        cancelProject() {
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        saveProject() {
            this.validate("newProjectObserver").then((response) => {
                if (response) {
                    var teamLeadId = this.teamLeadId;
                    this.machineService.send({
                        type: "ADDING_APEX_PROJECT",
                        teamId: this.teamDetailView.id.toString(),
                        teamLeadId: teamLeadId,
                        project: this.newProject,
                    });
                    toaster.ShowSuccess("Apex Project was created successfully.");
                }
            });
        }
        showEditProject(project) {
            this.editApexProject.id = project.projectId;
            this.editApexProject.name = project.projectName;
            this.editApexProject.description = project.description;
            this.machineService.send({ type: "UPDATE_APEX_PROJECT" });
        }
        updateProject() {
            this.validate("editProjectObserver").then((response) => {
                if (response) {
                    this.machineService.send({ type: "UPDATING_APEX_PROJECT", payload: this.editApexProject });
                }
            });
        }
        switchToTeamLead(projectId) {
            ClientModule.SwitchProjectOwnerToTeamLead({
                projectId: projectId,
                teamLeadId: this.teamLeadId,
            });
        }
        cancelDelete() {
            this.showDeleteMemberModal = false;
            this.showDeleteTeamModal = false;
            this.machineService.send({ type: "CLOSE_MODAL" });
        }
        // Computed
        get teamGridView() {
            return ClientModule.teamsGridView;
        }
        get teamDetailView() {
            return ClientModule.team;
        }
        get eligableTeamMember() {
            return ClientModule.eligableTeamMember;
        }
        // Team-level save / delete states. The initial teams fetch keeps its own full Loader;
        // opening a card uses that card's loader (isTeamCardLoading); member actions use the Members tab loader.
        get isTeamActionLoading() {
            return [
                "addingTeamInfo",
                "updatingTeamInfo",
                "deactivatingTeamInfo",
                "activatingTeamInfo",
                "deletingTeamInfo",
                "addingApexProject",
                "updatingApexProject",
            ].some((s) => this.state.matches(`loadedClientContactsInfo.contactAndTeamInfo.${s}`));
        }
        get isViewingTeamDetail() {
            return this.state.matches("loadedClientContactsInfo.contactAndTeamInfo.viewTeamInfo");
        }
        isTeamCardLoading(teamId) {
            return this.isViewingTeamDetail && this.viewingTeamId === teamId;
        }
        // Member actions, including the get-team-detail re-fetch after add/edit/remove. Scoped to the
        // Members tab so its content (and the new/edited row) stays visible underneath.
        get isMembersLoading() {
            return ["eligibleTeamMember", "updatingTeamMember", "deletingTeamMember", "refreshingTeamInfo"].some((s) => this.state.matches(`loadedClientContactsInfo.contactAndTeamInfo.${s}`));
        }
        // Add Member save in flight: shown inside the Add Member modal.
        get isSavingMember() {
            return this.state.matches("loadedClientContactsInfo.contactAndTeamInfo.addingTeamMember");
        }
        get teamLeadId() {
            var tar = this.teamDetailView.teamMembers.findIndex((x) => x.isTeamLead == true);
            if (tar !== -1) {
                return this.teamDetailView.teamMembers[tar].employeeId;
            }
            return null;
        }
        get showProjectTab() {
            var tar = this.teamDetailView.teamMembers.findIndex((x) => x.isTeamLead == true);
            if (tar !== -1 || this.teamDetailView.apexProjects.length > 0) {
                return false;
            }
            return true;
        }
        get hasTeamLead() {
            var tar = this.teamDetailView.teamMembers.findIndex((x) => x.isTeamLead == true);
            if (tar !== -1) {
                return false;
            }
            return true;
        }
        // The card shows the first five member photos, with the team lead first. Anyone past that is a +N badge.
        memberAvatarUrls(team) {
            return (team.memberImageURLs || []).slice(0, this.memberAvatarLimit);
        }
        isTeamLeadAvatar(team, index) {
            const name = (team.memberNames || [])[index] || "";
            return index === 0 && !!team.teamLeadName && name === team.teamLeadName;
        }
        remainingMemberCount(team) {
            const total = Number(team.teamMembersCount);
            if (!total || total <= this.memberAvatarLimit) {
                return 0;
            }
            return total - this.memberAvatarLimit;
        }
        memberName(team, index) {
            const name = (team.memberNames || [])[index] || "";
            if (this.isTeamLeadAvatar(team, index)) {
                return `${name} (Team Lead)`;
            }
            return name;
        }
        // The API always returns a URL. With no upload that URL is the shared user-default.svg.
        hasProfilePhoto(url) {
            const value = (url || "").trim();
            return !!value && value.toLowerCase().indexOf("user-default.svg") === -1;
        }
    };
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], ClientTeamsTabComponent.prototype, "client", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", typeof (_a = typeof Guid !== "undefined" && Guid) === "function" ? _a : Object)
    ], ClientTeamsTabComponent.prototype, "clientId", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], ClientTeamsTabComponent.prototype, "state", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], ClientTeamsTabComponent.prototype, "machineService", void 0);
    __decorate([
        Watch("state"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], ClientTeamsTabComponent.prototype, "onStateChange", null);
    ClientTeamsTabComponent = __decorate([
        Component({
            name: "ClientTeamsTabComponent",
            components: {
                Loader,
                SectionLoader,
                Simplebar,
                Multiselect,
                DeleteComponent,
                ECAlert,
                TeamOperationsComponent,
                TeamCorrespondenceComponent,
            },
        })
    ], ClientTeamsTabComponent);
    return ClientTeamsTabComponent;
})();
export default ClientTeamsTabComponent;
